<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { storeToRefs } from 'pinia'

import type { MockupCategoryId } from '@/constants/mockupDevices'
import { useMockupEditorStore } from '@/stores/mockupEditor'

const store = useMockupEditorStore()
const { mockupCategory, deviceSearchQuery, filteredDeviceTemplates, selectedTemplateId } = storeToRefs(store)

/** 分类列表（图标 + i18n key） */
const categories: { id: MockupCategoryId; key: string }[] = [
  { id: 'phone', key: 'leftRail.categoryPhone' },
  { id: 'tablet', key: 'leftRail.categoryTablet' },
  { id: 'laptop', key: 'leftRail.categoryLaptop' },
  { id: 'watch', key: 'leftRail.categoryWatch' },
  { id: 'other', key: 'leftRail.categoryOther' },
]

/** 自定义 select 下拉开关 */
const dropOpen = ref(false)
const dropRef = ref<HTMLElement | null>(null)

/** 点击外部关闭下拉（原生事件实现） */
function onDocClick(e: MouseEvent): void {
  if (dropRef.value && !dropRef.value.contains(e.target as Node)) {
    dropOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', onDocClick, { capture: true }))
onUnmounted(() => document.removeEventListener('click', onDocClick, { capture: true }))

/**
 * 选择分类并关闭下拉。
 *
 * @param id - 分类 id
 */
function selectCategory(id: MockupCategoryId): void {
  store.setMockupCategory(id)
  dropOpen.value = false
}
</script>

<template>
  <aside class="rail" :aria-label="store.tUi('leftRail.deviceLibrary')">
    <header class="hdr">
      <h2 class="h">{{ store.tUi('leftRail.mockupType') }}</h2>

      <!-- 自定义 select：图标 + 标题 左右排列 -->
      <div ref="dropRef" class="selWrap">
        <!-- 触发按钮（显示当前选中分类） -->
        <button
          type="button"
          class="selTrigger"
          :aria-haspopup="'listbox'"
          :aria-expanded="dropOpen ? 'true' : 'false'"
          :aria-label="store.tUi('leftRail.mockupCategory')"
          @click="dropOpen = !dropOpen"
        >
          <!-- Current category icon -->
          <span class="selIcon" aria-hidden="true">
            <svg v-if="mockupCategory === 'phone'" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <rect x="5" y="2" width="14" height="20" rx="3" /><circle cx="12" cy="18" r="1" fill="currentColor" stroke="none" />
            </svg>
            <svg v-else-if="mockupCategory === 'tablet'" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <rect x="4" y="2" width="16" height="20" rx="2" /><circle cx="12" cy="18.5" r="1" fill="currentColor" stroke="none" />
            </svg>
            <svg v-else-if="mockupCategory === 'laptop'" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="12" rx="2" /><path d="M2 19h20" />
            </svg>
            <svg v-else-if="mockupCategory === 'watch'" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <rect x="7" y="6" width="10" height="12" rx="3" />
              <path d="M9 6V4.5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1V6M9 18v1.5a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V18" />
              <path d="M12 9v3l1.5 1.5" />
            </svg>
            <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 9h18" />
            </svg>
          </span>
          <span class="selLabel">{{ store.tUi(categories.find(c => c.id === mockupCategory)?.key ?? 'leftRail.categoryPhone') }}</span>
          <svg class="selChevron" :class="{ open: dropOpen }" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        <!-- 下拉选项列表 -->
        <ul v-if="dropOpen" class="selList" role="listbox" :aria-label="store.tUi('leftRail.mockupCategory')">
          <li
            v-for="cat in categories"
            :key="cat.id"
            class="selOption"
            :class="{ active: mockupCategory === cat.id }"
            role="option"
            :aria-selected="mockupCategory === cat.id ? 'true' : 'false'"
            @click="selectCategory(cat.id)"
            @keydown.enter.prevent="selectCategory(cat.id)"
            @keydown.space.prevent="selectCategory(cat.id)"
          >
            <span class="optIcon" aria-hidden="true">
              <svg v-if="cat.id === 'phone'" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="3" /><circle cx="12" cy="18" r="1" fill="currentColor" stroke="none" />
              </svg>
              <svg v-else-if="cat.id === 'tablet'" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                <rect x="4" y="2" width="16" height="20" rx="2" /><circle cx="12" cy="18.5" r="1" fill="currentColor" stroke="none" />
              </svg>
              <svg v-else-if="cat.id === 'laptop'" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="12" rx="2" /><path d="M2 19h20" />
              </svg>
              <svg v-else-if="cat.id === 'watch'" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                <rect x="7" y="6" width="10" height="12" rx="3" />
                <path d="M9 6V4.5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1V6M9 18v1.5a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V18" />
                <path d="M12 9v3l1.5 1.5" />
              </svg>
              <svg v-else viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 9h18" />
              </svg>
            </span>
            <span class="optLabel">{{ store.tUi(cat.key) }}</span>
            <svg v-if="mockupCategory === cat.id" class="optCheck" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M5 12l5 5L19 7" />
            </svg>
          </li>
        </ul>
      </div>
    </header>

    <div class="searchRow">
      <input
        v-model="deviceSearchQuery"
        class="search"
        type="search"
        :placeholder="`${store.tUi('leftRail.searchDevices')}…`"
        :aria-label="store.tUi('leftRail.searchDevices')"
        autocomplete="off"
      />
      <button
        type="button"
        class="add"
        :aria-label="store.tUi('leftRail.addSelected')"
        @click="() => store.addSelectedDeviceToCanvas()"
      >
        {{ store.tUi('leftRail.add') }}
      </button>
    </div>

    <div class="grid" role="list">
      <div
        v-for="d in filteredDeviceTemplates"
        :key="d.id"
        class="card"
        :class="{ active: d.id === selectedTemplateId }"
        role="button"
        tabindex="0"
        :aria-pressed="d.id === selectedTemplateId ? 'true' : 'false'"
        @click="store.selectTemplate(d.id)"
        @dblclick.prevent="store.addSelectedDeviceToCanvas(d.id)"
        @keydown.enter.prevent="store.selectTemplate(d.id)"
        @keydown.space.prevent="store.selectTemplate(d.id)"
      >
        <div class="thumb" aria-hidden="true">
          <img v-if="d.thumbUrl" class="thumbImg" :src="d.thumbUrl" alt="" />
          <div v-else class="phone" />
        </div>

        <div class="cap">
          <span v-if="d.category !== 'other'" class="res">{{ d.width }} × {{ d.height }}</span>
          <span v-else class="resTag">{{ d.id === 'image-plane' ? store.tUi('leftRail.tagImageVideo') : store.tUi('leftRail.tagText') }}</span>
          <span class="name">{{ d.name }}</span>
        </div>
      </div>
    </div>
  </aside>
</template>

<style scoped>
.rail {
  width: var(--panel-left, var(--im-rail-w));
  max-width: 100%;
  border-right: 1px solid var(--im-border);
  background: var(--im-surface);
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 0.65rem 0.6rem 0.75rem;
  min-height: 0;
  overflow: auto;
  container-type: inline-size;
  container-name: imockup-left-rail;
}

.hdr {
  display: grid;
  gap: 0.45rem;
}

.h {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--im-muted-2);
}

/* 自定义 select 容器（相对定位，供下拉定位用） */
.selWrap {
  position: relative;
}

/* 触发按钮 */
.selTrigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  padding: 0.5rem 0.55rem;
  font-weight: 750;
  font-size: 0.875rem;
  color: var(--im-text);
  cursor: pointer;
  outline: none;
  text-align: left;
}

.selTrigger:focus-visible {
  border-color: var(--im-accent);
  box-shadow: 0 0 0 3px var(--im-focus-ring);
}

.selIcon {
  display: flex;
  align-items: center;
  color: var(--im-muted);
  flex-shrink: 0;
}

.selLabel {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selChevron {
  flex-shrink: 0;
  color: var(--im-muted);
  transition: transform 150ms ease;
}

.selChevron.open {
  transform: rotate(180deg);
}

/* 下拉列表 */
.selList {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 100;
  list-style: none;
  margin: 0;
  padding: 0.3rem;
  border-radius: 12px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);
}

/* 每个选项 */
.selOption {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 650;
  color: var(--im-text);
  transition: background 100ms ease;
  user-select: none;
}

.selOption:hover {
  background: var(--im-elevated);
}

.selOption.active {
  color: var(--im-accent);
  background: color-mix(in srgb, var(--im-accent) 8%, var(--im-surface));
}

.optIcon {
  display: flex;
  align-items: center;
  color: var(--im-muted);
  flex-shrink: 0;
}

.selOption.active .optIcon {
  color: var(--im-accent);
}

.optLabel {
  flex: 1;
}

.optCheck {
  flex-shrink: 0;
  color: var(--im-accent);
}

/* 搜索行 */
.searchRow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.45rem;
  align-items: center;
}

.search {
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  padding: 0.5rem 0.55rem;
  font-size: 0.875rem;
  outline: none;
}

.search:focus-visible {
  border-color: var(--im-accent);
  box-shadow: 0 0 0 3px var(--im-focus-ring);
}

.add {
  border-radius: 10px;
  border: 1px solid var(--im-interactive-bg);
  background: var(--im-interactive-bg);
  color: var(--im-interactive-fg);
  font-weight: 850;
  font-size: 0.8125rem;
  padding: 0.5rem 0.65rem;
  cursor: pointer;
  white-space: nowrap;
}

.add:hover {
  filter: brightness(1.05);
}

.add:focus-visible {
  outline: 3px solid var(--im-focus-ring);
  outline-offset: 2px;
}

.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.55rem;
  align-content: start;
}

@container imockup-left-rail (max-width: 251px) {
  .grid {
    grid-template-columns: 1fr;
  }
}

.card {
  position: relative;
  text-align: left;
  border-radius: 12px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  padding: 0.55rem 0.5rem 0.5rem;
  cursor: pointer;
  display: grid;
  gap: 0.45rem;
  transition:
    border-color 160ms ease,
    box-shadow 160ms ease,
    transform 120ms ease;
}

.card:hover {
  border-color: var(--im-border-strong);
  box-shadow: var(--im-shadow);
}

.card:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: 2px;
}

.card:focus:not(:focus-visible) {
  outline: none;
}

.card.active {
  border-color: var(--im-accent);
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.12);
}

.card:active {
  transform: translateY(1px);
}

.pen {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  border: 1px solid var(--im-border);
  background: color-mix(in srgb, var(--im-surface) 92%, transparent);
  display: grid;
  place-items: center;
  color: var(--im-text);
  cursor: pointer;
  z-index: 2;
}

.pen:hover {
  background: var(--im-surface);
}

.thumb {
  height: var(--im-left-rail-thumb-h, 180px);
  border-radius: 10px;
  background: linear-gradient(180deg, var(--im-surface-1), var(--im-elevated));
  display: grid;
  place-items: center;
  overflow: hidden;
}

.thumbImg {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
  place-self: center;
  margin: auto;
}

.phone {
  width: 52px;
  height: 96px;
  border-radius: 10px;
  border: 2px solid var(--im-accent);
  background: linear-gradient(180deg, var(--im-surface), var(--im-surface-1));
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.12);
}

.cap {
  display: grid;
  gap: 0.15rem;
}

.res {
  font-size: 0.6875rem;
  color: var(--im-muted);
  font-variant-numeric: tabular-nums;
}

.resTag {
  font-size: 0.6875rem;
  color: var(--im-accent);
  font-weight: 700;
  letter-spacing: 0.02em;
}

.name {
  font-size: 0.8125rem;
  font-weight: 850;
  color: var(--im-text);
  line-height: 1.2;
}

@media (max-width: 900px) {
  .rail {
    width: 100%;
    border-right: 0;
    border-bottom: 1px solid var(--im-border);
    max-height: min(34vh, 320px);
  }

  .thumb {
    height: var(--im-left-rail-thumb-h-sm, 110px);
  }
}
</style>
